import { Button, Tag } from "@design";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, Puzzle, Wrench } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { listDataSourcesOptions, listSkillsOptions, listToolsOptions } from "@/api";
import { Text } from "@/components/Text";
import { useChatModels } from "@/features/chat/useChatModels";
import type { AgentDraftBody } from "./useAgentDraft";

const PROMPT_PREVIEW = 280;
const PAGE = { query: { page_size: 100 } };

const nameOf = (uuid: string, items?: { uuid: string; name: string }[]) =>
  items?.find((item) => item.uuid === uuid)?.name ?? uuid;

/** Model, purpose, instructions and what the agent can use, by name. */
export function AgentDraftSummary({ body, skillUuids }: Readonly<{ body: AgentDraftBody; skillUuids: string[] }>) {
  const { t } = useTranslation("buildChat", { keyPrefix: "agent" });
  const [showPrompt, setShowPrompt] = useState(false);
  const models = useChatModels();
  const toolUuids = (body.toolConfigs ?? []).map((tc) => tc.toolUuid);
  const sourceUuids = body.dataCollectionIds ?? [];
  // Same queries (and cache) as the agent's Capabilities tab, only when the draft names any.
  const tools = useQuery({ ...listToolsOptions(PAGE), select: (r) => r.data, enabled: toolUuids.length > 0 });
  const sources = useQuery({ ...listDataSourcesOptions(PAGE), select: (r) => r.data, enabled: sourceUuids.length > 0 });
  const skills = useQuery({ ...listSkillsOptions(PAGE), select: (r) => r.data, enabled: skillUuids.length > 0 });
  const prompt = body.systemPrompt ?? "";
  const long = prompt.length > PROMPT_PREVIEW;
  const model = body.modelId ? (models.options.find((o) => o.id === body.modelId)?.label ?? body.modelId) : null;

  return (
    <div className="space-y-2">
      {model && (
        <Text variant="body-sm" tone="secondary">
          {t("model", { model })}
        </Text>
      )}
      {body.description && <Text variant="body-sm">{body.description}</Text>}
      {prompt && (
        <div className="space-y-1">
          <Text variant="caption" tone="tertiary">
            {t("instructions")}
          </Text>
          <Text variant="body-sm" tone="secondary" className="whitespace-pre-wrap break-words">
            {showPrompt || !long ? prompt : `${prompt.slice(0, PROMPT_PREVIEW)}…`}
          </Text>
          {long && (
            <Button variant="ghost" size="sm" onClick={() => setShowPrompt((open) => !open)}>
              {showPrompt ? t("hidePrompt") : t("showPrompt")}
            </Button>
          )}
        </div>
      )}
      {toolUuids.length + sourceUuids.length + skillUuids.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {toolUuids.map((uuid) => (
            <Tag key={uuid} icon={<Wrench size={12} />}>
              {nameOf(uuid, tools.data)}
            </Tag>
          ))}
          {sourceUuids.map((uuid) => (
            <Tag key={uuid} icon={<BookOpen size={12} />}>
              {nameOf(uuid, sources.data)}
            </Tag>
          ))}
          {skillUuids.map((uuid) => (
            <Tag key={uuid} icon={<Puzzle size={12} />}>
              {nameOf(uuid, skills.data)}
            </Tag>
          ))}
        </div>
      )}
    </div>
  );
}
